import React from 'react';
import { createPortal } from 'react-dom';
import { Archive, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';

interface ArchivePropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Pre-computed effects from current property state — only the true ones render. */
  effects: {
    unpublish: boolean;
    unshare: boolean;
    changeStatus: boolean;
  };
}

export const ArchivePropertyModal: React.FC<ArchivePropertyModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  effects,
}) => {
  const { theme } = useTheme();
  const isLight = theme !== 'dark';

  if (!isOpen) return null;

  const rows: string[] = [];
  if (effects.unpublish) rows.push('Se quitará de la publicación');
  if (effects.unshare) rows.push('Se quitará del Intercambio');
  if (effects.changeStatus) rows.push('El estado pasará a "Fuera de mercado"');

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className={clsx(
          'relative w-full max-w-md rounded-2xl border shadow-2xl',
          isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-700'
        )}
      >
        <div className={clsx('p-5 border-b flex items-center gap-3', isLight ? 'border-slate-100' : 'border-white/5')}>
          <div className={clsx('p-2.5 rounded-xl', isLight ? 'bg-amber-100 text-amber-600' : 'bg-amber-500/15 text-amber-400')}>
            <Archive size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={clsx('text-base font-bold', isLight ? 'text-slate-900' : 'text-white')}>
              Archivar propiedad
            </h3>
            <p className={clsx('text-xs', isLight ? 'text-slate-500' : 'text-slate-400')}>
              {rows.length > 0 ? 'Esto pasará al archivar:' : 'Confirma para continuar.'}
            </p>
          </div>
        </div>

        <div className="p-5">
          {rows.length > 0 && (
            <ul className="space-y-2.5 mb-4">
              {rows.map((row) => (
                <li key={row} className={clsx('flex items-start gap-2.5 text-sm', isLight ? 'text-slate-700' : 'text-slate-300')}>
                  <Check size={16} className={clsx('mt-0.5 shrink-0', isLight ? 'text-emerald-600' : 'text-emerald-400')} />
                  <span>{row}</span>
                </li>
              ))}
            </ul>
          )}
          <p className={clsx('text-xs', isLight ? 'text-slate-500' : 'text-slate-400')}>
            Podrás restaurarla desde la pestaña Archivadas.
          </p>
        </div>

        <div className={clsx('px-5 pb-5 flex gap-2 justify-end')}>
          <button
            onClick={onClose}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
              isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-slate-300 hover:bg-white/10'
            )}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors',
              isLight ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-500 hover:bg-amber-400'
            )}
          >
            Archivar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
